import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const OurOffices = () => {
  return (
    <>
      <Helmet>
        <title>Our Offices | Fort Worth Dental - Family & Cosmetic Dentistry</title>
        <meta name="description" content="Visit Fort Worth Dental at 4620 Citylake Blvd W, Fort Worth TX 76132. Dr. Arthur Mund, Dr. Joshua Pham, and Dr. Olena Razo provide expert dental care. Call (817) 985-3337." />
      </Helmet>
      
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[400px] bg-cover bg-center" style={{backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/images/office/building-exterior.png')"}}>
          <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">Fort Worth Dental Offices – Family & Cosmetic Dentistry</h1>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            
            {/* Welcome Section */}
            <section className="mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    src="/images/office/reception-desk.png" 
                    alt="Fort Worth Dental reception desk - friendly staff helping patient check in" 
                    className="rounded-lg shadow-xl w-full"
                  />
                </div>
                <div>
                  <h2 className="text-4xl font-bold text-gray-800 mb-6">Welcome To Fort Worth Dental!</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    Thank you for choosing Fort Worth Dental as your trusted dental care provider in the Fort Worth area.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    <strong>Dr. Arthur Mund, Dr. Joshua Pham and Dr. Olena Razo</strong>, along with our team of skilled dental professionals, 
                    are dedicated to providing comprehensive dental care. Whether you need <strong>dental implants</strong>, <strong>Invisalign clear aligners</strong>, 
                    <strong>cosmetic dentistry</strong>, porcelain veneers, dental crowns, teeth whitening, or routine cleanings and checkups – 
                    we deliver personalized treatment plans tailored to your unique smile goals.
                  </p>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    As your neighborhood <strong>family dentist in Fort Worth</strong>, we're committed to exceptional patient care. 
                    Our practice combines advanced dental technology with a warm, welcoming atmosphere. 
                    From your first visit, you'll experience why patients throughout the DFW metroplex trust us with their smiles.
                  </p>
                </div>
              </div>
            </section>

            {/* Helpful Hints Section */}
            <section className="mb-16 bg-teal-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">What to Bring to Your First Dental Appointment</h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                To ensure a smooth first visit, please bring your <strong>dental insurance card</strong>, a valid ID, 
                and a list of any medications you're currently taking. This helps us provide the most accurate treatment recommendations.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                From the moment you walk through our doors, you'll be greeted by friendly faces in a comfortable, 
                modern dental office. Our team is passionate about helping Fort Worth families achieve and maintain 
                <strong> healthy, beautiful smiles</strong> that last a lifetime.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed font-semibold text-teal-700">
                We look forward to welcoming you to our dental family!
              </p>
            </section>

            {/* Areas Served Section */}
            <section className="mb-16">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Proudly Serving the Greater Fort Worth Metroplex</h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Our <strong>Fort Worth dental office</strong> welcomes patients from throughout the DFW area. Whether you're looking for a 
                <strong> dentist near you</strong> for routine checkups, need <strong>emergency dental care</strong>, or want to explore 
                <strong> cosmetic dentistry options</strong> like teeth whitening or veneers – we're conveniently located to serve:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <span className="bg-gray-100 px-4 py-2 rounded-lg text-center text-gray-700 font-medium">Arlington, TX</span>
                <span className="bg-gray-100 px-4 py-2 rounded-lg text-center text-gray-700 font-medium">Benbrook, TX</span>
                <span className="bg-gray-100 px-4 py-2 rounded-lg text-center text-gray-700 font-medium">Lake Worth, TX</span>
                <span className="bg-gray-100 px-4 py-2 rounded-lg text-center text-gray-700 font-medium">Haltom City, TX</span>
                <span className="bg-gray-100 px-4 py-2 rounded-lg text-center text-gray-700 font-medium">Burleson, TX</span>
                <span className="bg-gray-100 px-4 py-2 rounded-lg text-center text-gray-700 font-medium">Mansfield, TX</span>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                Ready to schedule? Call us today or book online! Don't forget to check our{' '}
                <Link to="/specials" className="text-teal-600 hover:underline font-semibold">new patient specials</Link>{' '}
                including <strong>free dental implant consultations</strong> and <strong>Invisalign consultations</strong>!
              </p>
            </section>

            {/* Office Info */}
            <section className="mb-16 bg-gray-50 p-12 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Location</h3>
                  <div className="space-y-3 text-gray-700">
                    <p className="flex items-start">
                      <span className="text-teal-600 mr-2">📍</span>
                      <span>4620 Citylake Blvd W<br/>Fort Worth, TX 76132</span>
                    </p>
                    <p className="flex items-center">
                      <span className="text-teal-600 mr-2">📞</span>
                      <a href="tel:8179853337" className="text-teal-600 hover:text-teal-700 font-semibold">(817) 985-3337</a>
                    </p>
                    <p className="flex items-center">
                      <span className="text-teal-600 mr-2">📞</span>
                      <span className="text-sm">Implant Center:</span>
                      <a href="tel:8178544329" className="text-teal-600 hover:text-teal-700 font-semibold ml-1">(817) 854-4329</a>
                    </p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Office Hours</h3>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span className="font-semibold">Monday:</span>
                      <span>9:00am – 5:00pm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Tuesday:</span>
                      <span>9:00am – 6:00pm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Wednesday:</span>
                      <span>7:00am – 5:00pm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Thursday:</span>
                      <span>9:00am – 6:00pm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Friday:</span>
                      <span>7:00am – 2:00pm</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Map */}
            <section className="mb-16">
              <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">Find Us</h3>
              <div className="rounded-lg overflow-hidden shadow-xl h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3357.024917804545!2d-97.43825968481577!3d32.72836098098776!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e6d3c5d5d5d5d%3A0x5d5d5d5d5d5d5d5d!2s4620%20Citylake%20Blvd%20W%2C%20Fort%20Worth%2C%20TX%2076132!5e0!3m2!1sen!2sus!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Fort Worth Dental office location map - 4620 Citylake Blvd W Fort Worth TX 76132"
                />
              </div>
            </section>

            {/* Office Gallery */}
            <section className="mb-16">
              <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">Our Office</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <img 
                    src="/images/office/building-exterior.png" 
                    alt="Fort Worth Dental office building exterior at 4620 Citylake Blvd W Fort Worth TX" 
                    className="rounded-lg shadow-xl w-full h-72 object-cover"
                  />
                  <p className="text-center text-gray-600 mt-2">Our Office Building</p>
                </div>
                <div>
                  <img 
                    src="/images/office/reception-desk.png" 
                    alt="Fort Worth Dental welcoming reception area with friendly staff helping patients" 
                    className="rounded-lg shadow-xl w-full h-72 object-cover"
                  />
                  <p className="text-center text-gray-600 mt-2">Welcoming Reception Area</p>
                </div>
              </div>
            </section>

            {/* CTA */}
            <div className="bg-teal-600 text-white p-12 rounded-lg text-center">
              <h2 className="text-3xl font-bold mb-4">Visit Us Today!</h2>
              <p className="text-xl mb-8">Experience the Fort Worth Dental difference</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block">
                  Call (817) 985-3337
                </a>
                <Link to="/specials" className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700 inline-block">
                  View Special Offers
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default OurOffices;
